import type { CSSProperties } from 'react';
import { formatShare } from '@/lib/format';
import styles from './MekkoChart.module.css';

export interface MekkoItem {
  key: string;
  label: string;
  /** Sets the column's width, as its share of all widths (e.g. subscribers). */
  width: number;
  /** Sets the bar's height against the tallest (e.g. ARPU). */
  height: number;
  /** Colour token. */
  color: string;
  /** User-picked — outlined gold. */
  highlight?: boolean;
}

export interface MekkoChartProps {
  items: MekkoItem[];
  /** "Subscribers" — what the widths are. */
  widthLabel: string;
  /** Formats a height ("$9.10"). */
  formatHeight: (value: number) => string;
  /** What width × height means ("of revenue"), shown inside each bar as a share. */
  areaLabel: string;
  heightTitle: string;
}

/**
 * Variable-width bars (a Marimekko): each column is as wide as its share of
 * one measure and as tall as another, so its area is their product. For
 * "how many × how much each" — subscribers × ARPU = revenue by region.
 */
export function MekkoChart({ items, widthLabel, formatHeight, areaLabel, heightTitle }: MekkoChartProps) {
  const totalWidth = items.reduce((a, i) => a + i.width, 0) || 1;
  const maxHeight = items.reduce((m, i) => Math.max(m, i.height), 0) || 1;
  const totalArea = items.reduce((a, i) => a + i.width * i.height, 0) || 1;

  return (
    <div className={styles.chart}>
      <span className={styles.yTitle}>{heightTitle}</span>
      <div className={styles.columns} role="list">
        {items.map((item) => {
          const widthShare = item.width / totalWidth;
          const areaShare = (item.width * item.height) / totalArea;
          return (
            <div
              key={item.key}
              role="listitem"
              className={styles.column}
              data-highlight={item.highlight || undefined}
              style={
                {
                  '--w': widthShare,
                  '--h': `${(item.height / maxHeight) * 100}%`,
                  '--color': item.color,
                } as CSSProperties
              }
              aria-label={`${item.label}: ${formatShare(widthShare, 0)} of ${widthLabel.toLowerCase()}, ${formatHeight(item.height)}, ${formatShare(areaShare, 0)} ${areaLabel}`}
            >
              <div className={styles.plot}>
                <div className={styles.bar}>
                  <span className={styles.height}>{formatHeight(item.height)}</span>
                  <span className={styles.area}>
                    {formatShare(areaShare, 0)}
                    <span className={styles.areaLabel}>{areaLabel}</span>
                  </span>
                </div>
              </div>
              <div className={styles.foot}>
                <span className={styles.name}>{item.label}</span>
                <span className={styles.width}>
                  {formatShare(widthShare, 0)} of {widthLabel.toLowerCase()}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
